/**
 * Accessibility Store - חנות נגישות
 * Zustand store for the user-facing accessibility menu (תפריט נגישות).
 * Settings persist to localStorage and are applied to <html> so they survive reloads.
 * Part of WCAG 2.1 AA / ת"י 5568 compliance.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** Font scale clamp — keeps text readable without breaking layout. */
export const MIN_FONT_SCALE = 1
export const MAX_FONT_SCALE = 1.6
const FONT_STEP = 0.1

export interface AccessibilitySettings {
  fontScale: number
  highContrast: boolean
  highlightLinks: boolean
  readableFont: boolean
  noAnimations: boolean
}

const DEFAULTS: AccessibilitySettings = {
  fontScale: 1,
  highContrast: false,
  highlightLinks: false,
  readableFont: false,
  noAnimations: false,
}

interface AccessibilityState extends AccessibilitySettings {
  increaseFont: () => void
  decreaseFont: () => void
  toggle: (key: 'highContrast' | 'highlightLinks' | 'readableFont' | 'noAnimations') => void
  reset: () => void
}

const clamp = (n: number) =>
  Math.min(MAX_FONT_SCALE, Math.max(MIN_FONT_SCALE, Math.round(n * 10) / 10))

export const useAccessibilityStore = create<AccessibilityState>()(
  persist(
    (set) => ({
      ...DEFAULTS,

      increaseFont: () => set((s) => ({ fontScale: clamp(s.fontScale + FONT_STEP) })),
      decreaseFont: () => set((s) => ({ fontScale: clamp(s.fontScale - FONT_STEP) })),
      toggle: (key) => set((s) => ({ [key]: !s[key] }) as Partial<AccessibilityState>),
      reset: () => set({ ...DEFAULTS }),
    }),
    {
      name: 'binat-a11y-storage',
    }
  )
)

/**
 * Apply the current settings to <html>. Called from the AccessibilityProvider
 * effect whenever settings change, and once on mount so persisted choices restore.
 */
export function applyAccessibilitySettings(s: AccessibilitySettings) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.style.setProperty('--a11y-font-scale', String(s.fontScale))
  root.classList.toggle('a11y-high-contrast', s.highContrast)
  root.classList.toggle('a11y-highlight-links', s.highlightLinks)
  root.classList.toggle('a11y-readable-font', s.readableFont)
  root.classList.toggle('a11y-no-animations', s.noAnimations)
}
